import type { UsageLimitName } from "@/features/auth/api/usage";
import { formatBytes, type LimitStatus } from "@/features/usage/constants";
import {
  useDailyMaps,
  useDailyPrompts,
  useWorkspaceStorage,
} from "@/features/usage/hooks/useLimit";
import { useMemo } from "react";

export type UsageWarning = LimitStatus & {
  name: UsageLimitName;
  label: string;
};

const approachingLabel = (name: UsageLimitName, remaining: number): string => {
  if (name === "workspaceStorage") {
    return `${formatBytes(remaining)} storage left`;
  }
  if (name === "dailyMaps") {
    return remaining === 1 ? "1 map left" : `${remaining} maps left`;
  }
  return remaining === 1 ? "1 query left" : `${remaining} queries left`;
};

const hitLabel = (name: UsageLimitName): string => {
  if (name === "workspaceStorage") return "Storage full";
  if (name === "dailyMaps") return "Maps limit reached";
  return "Query limit reached";
};

export const useUsageWarnings = (): UsageWarning[] => {
  const prompts = useDailyPrompts();
  const maps = useDailyMaps();
  const storage = useWorkspaceStorage();

  return useMemo(() => {
    const sources: Array<{
      name: UsageLimitName;
      isAtLimit: boolean;
      isApproachingLimit: boolean;
      remaining: number | null;
    }> = [
        {
          name: "dailyPrompts",
          isAtLimit: prompts.isAtLimit,
          isApproachingLimit: prompts.isApproachingLimit,
          remaining:
            prompts.limit && prompts.limit.total != null
              ? prompts.limit.total - prompts.limit.usage
              : null,
        },
        {
          name: "dailyMaps",
          isAtLimit: maps.isAtLimit,
          isApproachingLimit: maps.isApproachingLimit,
          remaining:
            maps.limit && maps.limit.total != null
              ? maps.limit.total - maps.limit.usage
              : null,
        },
        {
          name: "workspaceStorage",
          isAtLimit: storage.isAtLimit,
          isApproachingLimit: storage.isApproachingLimit,
          remaining:
            storage.limit && storage.limit.total != null
              ? storage.limit.total - storage.limit.usage
              : null,
        },
      ];

    return sources
      .filter((item) => item.isAtLimit || item.isApproachingLimit)
      .map((item) => ({
        name: item.name,
        isAtLimit: item.isAtLimit,
        isApproachingLimit: item.isApproachingLimit,
        label: item.isAtLimit
          ? hitLabel(item.name)
          : approachingLabel(item.name, Math.max(0, item.remaining ?? 0)),
      }))
      .sort((a, b) => Number(b.isAtLimit) - Number(a.isAtLimit));
  }, [prompts, maps, storage]);
};
